import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const SIZE = 10, CELL = 32, DIM = SIZE * CELL; // 320
// ladders (up) + snakes (down)
const JUMPS = {
  1: 38, 4: 14, 9: 31, 21: 42, 28: 84, 36: 44, 51: 67, 71: 91, 80: 100,
  16: 6, 47: 26, 49: 11, 56: 53, 62: 19, 64: 60, 87: 24, 93: 73, 95: 75, 98: 78,
};
const LADDERS = Object.entries(JUMPS).filter(([f, t]) => +t > +f).map(([f, t]) => [+f, +t]);
const SNAKES = Object.entries(JUMPS).filter(([f, t]) => +t < +f).map(([f, t]) => [+f, +t]);

const center = (n) => {
  const idx = n - 1;
  const row = Math.floor(idx / SIZE);
  let col = idx % SIZE;
  if (row % 2 === 1) col = SIZE - 1 - col;
  return { x: col * CELL + CELL / 2, y: DIM - (row * CELL + CELL / 2) };
};

const PLAYERS = [
  { name: "You", color: "#e63946", off: -7 },
  { name: "CPU", color: "#1d3557", off: 7 },
];

export default function SnakesAndLadders() {
  const canvasRef = useRef(null);
  const [pos, setPos] = useState([0, 0]);
  const [turn, setTurn] = useState(0);
  const [dice, setDice] = useState(null);
  const [msg, setMsg] = useState("Your roll!");
  const [rolling, setRolling] = useState(false);
  const [winner, setWinner] = useState(null);
  const posRef = useRef([0, 0]);

  useEffect(() => { posRef.current = pos; }, [pos]);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    // board squares
    for (let n = 1; n <= 100; n++) {
      const c = center(n);
      const dark = (Math.floor((n - 1) / SIZE) + ((n - 1) % SIZE)) % 2 === 0;
      ctx.fillStyle = dark ? "#fdf6e3" : "#f2e2b8";
      ctx.fillRect(c.x - CELL / 2, c.y - CELL / 2, CELL, CELL);
      ctx.strokeStyle = "#c9a44a"; ctx.lineWidth = 1;
      ctx.strokeRect(c.x - CELL / 2, c.y - CELL / 2, CELL, CELL);
      ctx.fillStyle = "#8a5a2b"; ctx.font = "bold 10px monospace";
      ctx.fillText(String(n), c.x - CELL / 2 + 3, c.y - CELL / 2 + 11);
    }
    // ladders
    LADDERS.forEach(([f, t]) => {
      const a = center(f), b = center(t);
      ctx.strokeStyle = "#2a9d8f"; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(a.x - 5, a.y); ctx.lineTo(b.x - 5, b.y); ctx.moveTo(a.x + 5, a.y); ctx.lineTo(b.x + 5, b.y); ctx.stroke();
    });
    // snakes
    SNAKES.forEach(([f, t]) => {
      const a = center(f), b = center(t);
      ctx.strokeStyle = "#c1121f"; ctx.lineWidth = 5; ctx.lineCap = "round";
      ctx.beginPath(); ctx.moveTo(a.x, a.y);
      ctx.quadraticCurveTo((a.x + b.x) / 2 + 18, (a.y + b.y) / 2, b.x, b.y); ctx.stroke();
      ctx.fillStyle = "#c1121f"; ctx.beginPath(); ctx.arc(a.x, a.y, 5, 0, 7); ctx.fill();
    });
    // tokens
    pos.forEach((p, i) => {
      if (p < 1) return;
      const c = center(p);
      ctx.fillStyle = PLAYERS[i].color;
      ctx.beginPath(); ctx.arc(c.x + PLAYERS[i].off, c.y + 6, 7, 0, 7); ctx.fill();
      ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.stroke();
    });
  }, [pos]);

  const moveStepwise = (idx, from, to) => new Promise((resolve) => {
    let cur = from;
    const step = () => {
      if (cur >= to) { resolve(); return; }
      cur++;
      const np = posRef.current.slice(); np[idx] = cur; setPos(np); sfx.beep();
      setTimeout(step, 130);
    };
    step();
  });

  const takeTurn = async (idx) => {
    setRolling(true); setDice(null);
    await new Promise((r) => setTimeout(r, 250));
    const d = 1 + Math.floor(Math.random() * 6);
    setDice(d); sfx.click();
    await new Promise((r) => setTimeout(r, 350));
    const from = posRef.current[idx];
    let dest = from + d;
    if (dest > 100) { // overshoot: stay
      setMsg(`${PLAYERS[idx].name} rolled ${d} — need exact 100, stays put`);
    } else {
      await moveStepwise(idx, from, dest);
      if (JUMPS[dest]) {
        const jTo = JUMPS[dest];
        const climb = jTo > dest;
        setMsg(`${PLAYERS[idx].name} ${climb ? "climbs a ladder 🪜" : "slides down a snake 🐍"}!`);
        climb ? sfx.powerUp() : sfx.crash();
        const np = posRef.current.slice(); np[idx] = jTo; setPos(np);
        posRef.current = np; dest = jTo;
        await new Promise((r) => setTimeout(r, 400));
      }
      if (dest === 100) {
        setWinner(idx); sfx.win();
        setMsg(idx === 0 ? "🎉 You reached 100 and win!" : "CPU reached 100 first 😜");
        setRolling(false);
        return;
      }
    }
    const next = (idx + 1) % 2;
    setTurn(next);
    setRolling(false);
    if (next === 0) setMsg("Your roll!");
  };

  // CPU auto plays
  useEffect(() => {
    if (winner === null && turn === 1 && !rolling) {
      const t = setTimeout(() => { setMsg("CPU is rolling…"); takeTurn(1); }, 700);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn, winner]);

  const reset = () => {
    setPos([0, 0]); posRef.current = [0, 0]; setTurn(0); setDice(null); setWinner(null); setRolling(false); setMsg("Your roll!");
  };

  return (
    <div className="w-full text-center">
      <div className="mb-2 flex items-center justify-center gap-2">
        {PLAYERS.map((p, i) => (
          <span key={i} data-testid={`sl-tag-${i}`} className={`flex items-center gap-1.5 rounded-md border-2 px-2.5 py-1 text-sm font-bold ${turn === i && winner === null ? "border-kirana-brown bg-kirana-gold" : "border-kirana-border bg-white"}`}>
            <span className="h-3 w-3 rounded-full" style={{ background: p.color }} /> {p.name}: {pos[i]}
          </span>
        ))}
      </div>
      <div className="relative mx-auto w-full" style={{ maxWidth: DIM }}>
        <canvas ref={canvasRef} width={DIM} height={DIM} data-testid="sl-board" className="w-full rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "auto" }} />
        {winner !== null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/55 p-3">
            <AResult win={winner === 0} text={winner === 0 ? "You Win! 🎲" : "CPU Wins 😜"} testId="sl-over" />
            <ABtn tone="green" onClick={reset} className="mt-3" data-testid="sl-again">Play Again</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 min-h-[20px] text-sm font-bold text-kirana-brown">{msg}</p>
      <div className="mt-1 flex items-center justify-center gap-3">
        <div data-testid="sl-dice" className="flex h-12 w-12 items-center justify-center rounded-md border-[3px] border-kirana-brown bg-white font-signboard text-2xl font-bold text-kirana-red">{dice ?? "—"}</div>
        <ABtn tone="red" data-testid="sl-roll" disabled={rolling || turn !== 0 || winner !== null} onClick={() => takeTurn(0)}>Roll Dice</ABtn>
      </div>
    </div>
  );
}
